import React, { useState, useMemo } from "react";
import { PivotField, ExcelColumn, DateGrouping, ExcelData, PivotConfig } from "../types";
import { DropZone } from "./DropZone";
import { FilterValueSelector } from "./FilterValueSelector";
import { Select } from "../../../../../../ui/components/Select";

interface PivotBuilderCanvasProps {
  globalFilters: PivotField[];
  rowGroups: PivotField[];
  columnLabels: PivotField[];
  values: PivotField[];
  excelData?: ExcelData | null;
  onAddField: (zone: keyof PivotConfig, field: ExcelColumn, options?: any) => void;
  onRemoveField: (zone: keyof PivotConfig, fieldId: string) => void;
  onUpdateDateGrouping: (fieldId: string, grouping: DateGrouping) => void;
  onUpdateAggregation: (fieldId: string, aggregation: string) => void;
  onUpdateFilterValues: (fieldId: string, values: string[]) => void;
}

export const PivotBuilderCanvas: React.FC<PivotBuilderCanvasProps> = ({
  globalFilters,
  rowGroups,
  columnLabels,
  values,
  excelData,
  onAddField,
  onRemoveField,
  onUpdateDateGrouping,
  onUpdateAggregation,
  onUpdateFilterValues,
}) => {

  const handleDrop = (zone: keyof PivotConfig) => (field: ExcelColumn) => {
    // Add field directly without date grouping modal
    onAddField(zone as keyof PivotConfig, field);
  };

  // Extract unique values for each filter field
  const getFieldValues = (field: PivotField): string[] => {
    if (!excelData || !excelData.headers || !excelData.rows) {
      return [];
    }

    const columnIndex = excelData.headers.findIndex((h) => h === field.name);
    if (columnIndex === -1) return [];

    const values = new Set<string>();
    excelData.rows.forEach((row) => {
      const value = row[columnIndex];
      if (value !== null && value !== undefined && value !== "") {
        values.add(String(value));
      }
    });

    return Array.from(values).sort();
  };

  return (
    <div className="flex flex-col bg-default-background">
      {/* Main Builder Area */}
      <div className="flex gap-4 p-4">
        {/* Row Groups Zone (Left) */}
        <div className="w-64 flex-shrink-0">
          <DropZone
            title="Row Groups"
            description="Drop fields here to create row groups (supports nesting)"
            fields={rowGroups}
            onRemoveField={(id) => onRemoveField("rowGroups", id)}
            onDrop={handleDrop("rowGroups" as keyof PivotConfig)}
          />
        </div>

        {/* Column Labels & Values Zone (Right) */}
        <div className="flex-1 flex flex-col gap-4">
          {/* Column Labels Zone */}
          <DropZone
            title="Column Labels"
            description="Drop fields here to create column headers"
            fields={columnLabels}
            onRemoveField={(id) => onRemoveField("columnLabels", id)}
            onDrop={handleDrop("columnLabels" as keyof PivotConfig)}
          />

          {/* Values Zone */}
          <DropZone
            title="Values"
            description="Drop numeric fields here to aggregate"
            fields={values}
            onRemoveField={(id) => onRemoveField("values", id)}
            onDrop={handleDrop("values" as keyof PivotConfig)}
          >
            {values.map((field) => (
              <div key={field.id} className="mt-2">
                <Select
                  value={field.aggregation || "sum"}
                  onValueChange={(value) =>
                    onUpdateAggregation(field.id, value)
                  }
                  placeholder="Select aggregation"
                >
                  <Select.Item value="sum">Sum</Select.Item>
                  <Select.Item value="avg">Average</Select.Item>
                  <Select.Item value="count">Count</Select.Item>
                  <Select.Item value="min">Min</Select.Item>
                  <Select.Item value="max">Max</Select.Item>
                </Select>
              </div>
            ))}
          </DropZone>
        </div>
      </div>

    </div>
  );
};
